/**
 * The signed-in person's saved browser logins in one organisation (AMO-152),
 * for Settings → Security and the browser panel's first-use notice.
 *
 * Plain request functions over the workspace server's
 * `/api/amoreg/browser-logins` routes, which only ever answer for the
 * session's own logins in an organisation it belongs to. Site names and
 * dates only; cookie values never leave the server.
 *
 * @module state/browserLogins
 */
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

const SavedSiteSchema = Schema.Struct({
  site: Schema.String,
  changedAt: Schema.String,
  lastUsedAt: Schema.String,
});
export type SavedSite = typeof SavedSiteSchema.Type;

const BrowserLoginsSchema = Schema.Struct({
  /** False when this server does not run saved logins at all. */
  enabled: Schema.Boolean,
  /** False for someone who sees the organisation without belonging to it (a global admin). */
  member: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  /** The person's own switch. */
  on: Schema.Boolean,
  noticeSeen: Schema.Boolean,
  /** The organisation admin's switch; off overrides the person's own. */
  orgOn: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  sites: Schema.Array(SavedSiteSchema),
});
export type BrowserLogins = typeof BrowserLoginsSchema.Type;

const decode = Schema.decodeUnknownEffect(BrowserLoginsSchema);

// The helper sets `pathname` and encodes it, so the query must go in its own
// argument: a `?` inside the path arrives as `%3F` and the route never matches.
const url = (path: string, params: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/browser-logins${path}`, params);

const failure = async (response: Response, fallback: string) =>
  (await response.text().catch(() => "")) || fallback;

/** Null on failure, so the page shows an error rather than an empty list. */
export async function fetchBrowserLogins(organizationId: string): Promise<BrowserLogins | null> {
  try {
    const response = await fetch(url("", { organizationId }), { credentials: "include" });
    if (!response.ok) return null;
    return await Effect.runPromise(decode(await response.json()));
  } catch {
    return null;
  }
}

/** Forget one site, or every saved login when `site` is omitted; an error message, or null. */
export async function forgetBrowserLogins(
  organizationId: string,
  site?: string,
): Promise<string | null> {
  const fallback = site
    ? "That login could not be forgotten."
    : "Your logins could not be forgotten.";
  try {
    const response = await fetch(url("", site ? { organizationId, site } : { organizationId }), {
      method: "DELETE",
      credentials: "include",
    });
    return response.ok ? null : await failure(response, fallback);
  } catch {
    return fallback;
  }
}

/** Change the person's switch or record the notice as seen; an error message, or null. */
export async function updateBrowserLoginSettings(
  organizationId: string,
  change: { readonly on?: boolean; readonly noticeSeen?: true },
): Promise<string | null> {
  const fallback = "Your choice could not be saved.";
  try {
    const response = await fetch(url("/settings", { organizationId }), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(change),
    });
    return response.ok ? null : await failure(response, fallback);
  } catch {
    return fallback;
  }
}

/**
 * An organisation admin's switch for everyone. Off forgets every member's
 * saved logins. An error message, or null.
 */
export async function updateBrowserLoginOrganization(
  organizationId: string,
  on: boolean,
): Promise<string | null> {
  const fallback = "The organisation setting could not be saved.";
  try {
    const response = await fetch(url("/organization", { organizationId }), {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ on }),
    });
    return response.ok ? null : await failure(response, fallback);
  } catch {
    return fallback;
  }
}
